/**
 * RoleSelectionModal Component
 * Modal for new Google users to choose their role (player or club_owner)
 */
import { useState } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../services/api';
import { User, Building2 } from 'lucide-react';

const RoleSelectionModal = ({ isOpen, onComplete, userName }) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState(null);
  const [city, setCity] = useState('');
  const [level, setLevel] = useState('intermediate');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error('Veuillez choisir un type de compte');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(
        '/auth/google/complete-profile',
        {
          role: selectedRole,
          city: city || null,
          level: level
        },
        { withCredentials: true }
      );

      if (data.success) {
        toast.success('Profil complété avec succès !');
        onComplete(data.user);
      }
    } catch (err) {
      console.error('Profile completion error:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de la complétion du profil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="theme-bg-card border theme-border rounded-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold theme-text mb-2">
            Bienvenue {userName} !
          </h2>
          <p className="theme-text-secondary text-sm">
            Complétez votre profil pour commencer
          </p>
        </div>

        {/* Role Selection */}
        <div className="mb-6">
          <Label className="text-zinc-300 text-sm mb-3 block">
            Quel type de compte souhaitez-vous ?
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="role-player-btn"
              onClick={() => setSelectedRole('player')}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedRole === 'player'
                  ? 'border-[#00d4ff] bg-[#00d4ff]/10'
                  : 'theme-border hover:border-[#00d4ff]/50'
              }`}
            >
              <User className={`w-8 h-8 mx-auto mb-2 ${
                selectedRole === 'player' ? 'theme-accent' : 'theme-text-secondary'
              }`} />
              <div className={`font-semibold ${
                selectedRole === 'player' ? 'theme-accent' : 'theme-text'
              }`}>
                Joueur
              </div>
              <div className="text-xs theme-text-secondary mt-1">
                Rejoindre des matchs et tournois
              </div>
            </button>

            <button
              type="button"
              data-testid="role-club-owner-btn"
              onClick={() => setSelectedRole('club_owner')}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedRole === 'club_owner'
                  ? 'border-[#c8e600] bg-[#c8e600]/10'
                  : 'theme-border hover:border-[#c8e600]/50'
              }`}
            >
              <Building2 className={`w-8 h-8 mx-auto mb-2 ${
                selectedRole === 'club_owner' ? 'text-[#c8e600]' : 'theme-text-secondary'
              }`} />
              <div className={`font-semibold ${
                selectedRole === 'club_owner' ? 'text-[#c8e600]' : 'theme-text'
              }`}>
                Club
              </div>
              <div className="text-xs theme-text-secondary mt-1">
                Gérer mon club de padel
              </div>
            </button>
          </div>
        </div>

        {/* Additional fields for players */}
        {selectedRole === 'player' && (
          <div className="space-y-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-200">
            <div>
              <Label htmlFor="city" className="text-zinc-300">
                Ville (optionnel)
              </Label>
              <Input
                id="city"
                data-testid="role-city-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Ex: Paris"
                className="theme-bg-card theme-border theme-text focus:border-[#00d4ff] mt-1"
              />
            </div>

            <div>
              <Label htmlFor="level" className="text-zinc-300">
                Niveau
              </Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger 
                  data-testid="role-level-select"
                  className="theme-bg-card theme-border theme-text focus:border-[#00d4ff] mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="theme-bg-card theme-border">
                  <SelectItem value="beginner">{t('levels.beginner')}</SelectItem>
                  <SelectItem value="intermediate">{t('levels.intermediate')}</SelectItem>
                  <SelectItem value="advanced">{t('levels.advanced')}</SelectItem>
                  <SelectItem value="pro">{t('levels.pro')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          data-testid="role-submit-btn"
          disabled={!selectedRole || loading}
          className="w-full bg-[#00d4ff] text-black font-bold hover:bg-[#00b8d4] disabled:opacity-50"
        >
          {loading ? 'Enregistrement...' : 'Commencer'}
        </Button>
      </div>
    </div>
  );
};

export default RoleSelectionModal;
